import { createContext, useContext, useMemo, useRef, type ReactNode } from "react";
import packageJson from "../../../package.json";

import { TelemetryCollector, type UiTelemetryEvent } from "./index";

export interface TelemetryContextValue {
  bindContext: (context: {
    requestId: string;
    caseId: string;
    caseVersion: number;
  } | null) => Promise<void>;
  record: (
    eventName: string,
    fields: {
      route: string;
      component: string;
      outcome: string;
      caseVersion?: number;
      durationMilliseconds?: number;
    },
  ) => UiTelemetryEvent;
  snapshot: () => readonly UiTelemetryEvent[];
}

const TelemetryContext = createContext<TelemetryContextValue | null>(null);

export function TelemetryProvider({ children }: { children: ReactNode }) {
  const collector = useRef(new TelemetryCollector());
  const bindingSequence = useRef(0);
  const context = useRef<{
    requestIdentifier: string | null;
    caseIdentifierHash: string | null;
    caseVersion: number | null;
    status: "context_unbound" | "context_binding" | "context_bound" | "context_hash_unavailable";
  }>({ requestIdentifier: null, caseIdentifierHash: null, caseVersion: null, status: "context_unbound" });
  const value = useMemo<TelemetryContextValue>(() => ({
    bindContext: async (next) => {
      const sequence = ++bindingSequence.current;
      context.current = {
        requestIdentifier: next?.requestId ?? null,
        caseIdentifierHash: null,
        caseVersion: next?.caseVersion ?? null,
        status: next === null ? "context_unbound" : "context_binding",
      };
      if (next === null) return;
      try {
        // Hash only a server-generated random case identifier, never clinical text.
        const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(next.caseId));
        if (sequence !== bindingSequence.current) return;
        context.current = {
          requestIdentifier: next.requestId,
          caseIdentifierHash: `sha256:${Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")}`,
          caseVersion: next.caseVersion,
          status: "context_bound",
        };
      } catch {
        // Telemetry failure must not block urgent help. Explicitly expose missing
        // correlation instead of fabricating a hash or leaking the unhashed value.
        if (sequence === bindingSequence.current) context.current.status = "context_hash_unavailable";
      }
    },
    record: (eventName, fields) => collector.current.record(eventName, {
      eventIdentifier: crypto.randomUUID(),
      requestIdentifier: context.current.requestIdentifier,
      caseIdentifierHash: context.current.caseIdentifierHash,
      status: context.current.status,
      interfaceVersion: packageJson.version,
      route: fields.route,
      component: fields.component,
      outcome: fields.outcome,
      caseVersion: fields.caseVersion ?? context.current.caseVersion,
      ...(fields.durationMilliseconds === undefined ? {} : { durationMilliseconds: fields.durationMilliseconds }),
    }),
    snapshot: () => collector.current.snapshot(),
  }), []);
  return <TelemetryContext.Provider value={value}>{children}</TelemetryContext.Provider>;
}

export function useUiTelemetry(): TelemetryContextValue {
  const value = useContext(TelemetryContext);
  if (value === null) throw new Error("useUiTelemetry must be used within TelemetryProvider.");
  return value;
}
